/**
 * 공지사항 목록 페이지 (V5.15)
 *
 * V5.15 변경사항:
 * - 중요공지(isPinned=true): 항상 상단 고정, 페이지네이션 미적용
 * - 일반공지(isPinned=false): 10개 단위 페이지네이션
 * - 더보기 → 페이지 번호 방식으로 변경
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pin, Paperclip, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useAnnouncementsList,
  useAnnouncementPermission,
  AnnouncementListItem,
  AnnouncementBoardType,
  ANNOUNCEMENT_BOARD_LABELS,
  DEFAULT_ANNOUNCEMENT_BOARD_TYPE,
} from '../../shared/hooks/useAnnouncements';

/** V70G8C: 미확인 공지 표시 (LNB 배지와 같은 서버 규칙) */
function UnreadDot() {
  return (
    <span
      data-testid="announcement-unread-dot"
      aria-label="읽지 않음"
      title="읽지 않은 공지"
      style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', flexShrink: 0, display: 'inline-block' }}
    />
  );
}

export function AnnouncementsPage() {
  const navigate = useNavigate();
  // V64: 게시판 탭 상태 (기본값 'notice')
  const [boardType, setBoardType] = useState<AnnouncementBoardType>(DEFAULT_ANNOUNCEMENT_BOARD_TYPE);
  const {
    pinnedAnnouncements,
    regularAnnouncements,
    isLoading,
    error,
    currentPage,
    totalPages,
    totalRegularCount,
    goToPage,
  } = useAnnouncementsList(boardType);
  const { canWrite, isLoading: permissionLoading } = useAnnouncementPermission();
  // V70G8C: 목록 진입만으로 읽음 처리하지 않음 (읽음은 공지 상세에서 기록)

  // 날짜 포맷 (YYYY.MM.DD)
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  // V60: 국가 스코프 뱃지
  const companyCodeLabel = (code?: string) => {
    if (!code) return null;
    const labels: Record<string, { text: string; bg: string; color: string }> = {
      KR: { text: 'KR', bg: '#eff6ff', color: '#2563eb' },
      CN: { text: 'CN', bg: '#fef2f2', color: '#dc2626' },
      ALL: { text: '전체', bg: '#f0fdf4', color: '#16a34a' },
    };
    const info = labels[code];
    if (!info) return null;
    return (
      <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, background: info.bg, color: info.color, flexShrink: 0 }}>
        {info.text}
      </span>
    );
  };

  // 공지 클릭
  const handleItemClick = (item: AnnouncementListItem) => {
    navigate(`/announcements/${item.id}`);
  };

  // 새 공지 작성
  const handleCreate = () => {
    navigate('/announcements/new');
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', paddingTop: '40px' }}>
      {/* 헤더 - 제목과 작성 버튼을 한 줄에 배치 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: '16px',
        }}
      >
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 600,
            margin: 0,
            marginLeft: '12px',
            color: '#1f2937',
          }}
        >
          공지사항
        </h1>

        {/* 작성 버튼 */}
        {!permissionLoading && canWrite && (
          <button
            onClick={handleCreate}
            style={{
              padding: '7px 14px',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            <Plus size={15} strokeWidth={2.5} />
            작성
          </button>
        )}
      </div>

      {/* V64: 게시판 탭 */}
      <div
        style={{
          display: 'flex',
          gap: '2px',
          marginBottom: '12px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        {(Object.keys(ANNOUNCEMENT_BOARD_LABELS) as AnnouncementBoardType[]).map((bt) => {
          const active = bt === boardType;
          return (
            <button
              key={bt}
              onClick={() => {
                if (bt !== boardType) {
                  setBoardType(bt);
                }
              }}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
                marginBottom: '-1px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: active ? 600 : 500,
                color: active ? 'var(--color-primary)' : '#6b7280',
                letterSpacing: '-0.01em',
              }}
            >
              {ANNOUNCEMENT_BOARD_LABELS[bt]}
            </button>
          );
        })}
      </div>

      {/* 테이블 컨테이너 */}
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      >
        {/* 로딩 */}
        {isLoading && pinnedAnnouncements.length === 0 && regularAnnouncements.length === 0 && (
          <div
            style={{
              padding: '60px 24px',
              textAlign: 'center',
              color: '#9ca3af',
              fontSize: '14px',
            }}
          >
            불러오는 중...
          </div>
        )}

        {/* 에러 */}
        {error && (
          <div
            style={{
              padding: '60px 24px',
              textAlign: 'center',
              color: '#dc2626',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        {/* 빈 목록 */}
        {!isLoading && !error && pinnedAnnouncements.length === 0 && regularAnnouncements.length === 0 && (
          <div
            style={{
              padding: '60px 24px',
              textAlign: 'center',
              color: '#9ca3af',
              fontSize: '14px',
            }}
          >
            등록된 공지사항이 없습니다.
          </div>
        )}

        {/* 테이블 */}
        {(pinnedAnnouncements.length > 0 || regularAnnouncements.length > 0) && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif" }}>
            <thead>
              <tr>
                <th
                  style={{
                    padding: '12px 12px 12px 0',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#1f2937',
                    letterSpacing: '0.02em',
                    borderBottom: '1px solid var(--color-primary-light)',
                    background: 'var(--color-primary-lighter)',
                    width: '80px',
                  }}
                >
                  번호
                </th>
                <th
                  style={{
                    padding: '12px 12px 12px 340px',
                    textAlign: 'left',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#1f2937',
                    letterSpacing: '0.02em',
                    borderBottom: '1px solid var(--color-primary-light)',
                    background: 'var(--color-primary-lighter)',
                  }}
                >
                  제목
                </th>
                <th
                  style={{
                    padding: '12px 12px',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#1f2937',
                    letterSpacing: '0.02em',
                    borderBottom: '1px solid var(--color-primary-light)',
                    background: 'var(--color-primary-lighter)',
                    width: '120px',
                  }}
                >
                  작성자
                </th>
                <th
                  style={{
                    padding: '12px 12px',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#1f2937',
                    letterSpacing: '0.02em',
                    borderBottom: '1px solid var(--color-primary-light)',
                    background: 'var(--color-primary-lighter)',
                    width: '100px',
                  }}
                >
                  등록일
                </th>
              </tr>
            </thead>
            <tbody>
              {/* V5.15: 중요공지 먼저 렌더링 */}
              {pinnedAnnouncements.map((item, index) => (
                <tr
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  style={{
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    background: '#fffbeb', // 중요공지 배경색
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fef3c7';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fffbeb';
                  }}
                >
                  {/* 번호 */}
                  <td
                    style={{
                      padding: '14px 12px 14px 0',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#4b5563',
                      letterSpacing: '0.01em',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        padding: '3px 8px',
                        background: '#fef2f2',
                        color: '#dc2626',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                      }}
                    >
                      <Pin size={11} fill="#dc2626" />
                      중요
                    </span>
                  </td>

                  {/* 제목 */}
                  <td style={{ padding: '14px 12px 14px 0', textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {item.isUnread && <UnreadDot />}
                      <span
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#111827',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {item.title}
                      </span>
                      {companyCodeLabel(item.companyCode)}
                      {item.attachmentCount > 0 && (
                        <Paperclip
                          size={13}
                          style={{ color: '#9ca3af', flexShrink: 0 }}
                        />
                      )}
                    </div>
                  </td>

                  {/* 작성자 */}
                  <td
                    style={{
                      padding: '14px 12px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#4b5563',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {item.createdByName}
                  </td>

                  {/* 등록일 */}
                  <td
                    style={{
                      padding: '14px 12px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#6b7280',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {formatDate(item.createdAt)}
                  </td>
                </tr>
              ))}

              {/* V5.15: 일반공지 (현재 페이지) */}
              {regularAnnouncements.map((item, index) => (
                <tr
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  style={{
                    cursor: 'pointer',
                    borderBottom: index < regularAnnouncements.length - 1 ? '1px solid #f3f4f6' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {/* 번호 - V5.15: 역순 번호 (전체 기준) */}
                  <td
                    style={{
                      padding: '14px 12px 14px 0',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#4b5563',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {totalRegularCount - ((currentPage - 1) * 10 + index)}
                  </td>

                  {/* 제목 */}
                  <td style={{ padding: '14px 12px 14px 0', textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {item.isUnread && <UnreadDot />}
                      <span
                        style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#111827',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {item.title}
                      </span>
                      {companyCodeLabel(item.companyCode)}
                      {item.attachmentCount > 0 && (
                        <Paperclip
                          size={13}
                          style={{ color: '#9ca3af', flexShrink: 0 }}
                        />
                      )}
                    </div>
                  </td>

                  {/* 작성자 */}
                  <td
                    style={{
                      padding: '14px 12px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#4b5563',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {item.createdByName}
                  </td>

                  {/* 등록일 */}
                  <td
                    style={{
                      padding: '14px 12px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#6b7280',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {formatDate(item.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* V5.15: 페이지네이션 UI */}
        {totalPages > 1 && (
          <div
            style={{
              padding: '16px',
              borderTop: '1px solid #f3f4f6',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {/* 이전 버튼 */}
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              style={{
                padding: '6px 10px',
                background: currentPage === 1 ? '#f3f4f6' : 'white',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: currentPage === 1 || isLoading ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                fontSize: '13px',
              }}
            >
              <ChevronLeft size={16} />
            </button>

            {/* 페이지 번호들 */}
            {(() => {
              const pages: number[] = [];
              const maxVisible = 5;
              let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
              let end = Math.min(totalPages, start + maxVisible - 1);

              if (end - start + 1 < maxVisible) {
                start = Math.max(1, end - maxVisible + 1);
              }

              for (let i = start; i <= end; i++) {
                pages.push(i);
              }

              return pages.map((page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  disabled={isLoading}
                  style={{
                    padding: '6px 12px',
                    background: page === currentPage ? 'var(--color-primary)' : 'white',
                    color: page === currentPage ? 'white' : '#374151',
                    border: page === currentPage ? 'none' : '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: isLoading ? 'default' : 'pointer',
                    fontSize: '13px',
                    fontWeight: page === currentPage ? 600 : 400,
                    minWidth: '36px',
                  }}
                >
                  {page}
                </button>
              ));
            })()}

            {/* 다음 버튼 */}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              style={{
                padding: '6px 10px',
                background: currentPage === totalPages ? '#f3f4f6' : 'white',
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: currentPage === totalPages || isLoading ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                fontSize: '13px',
              }}
            >
              <ChevronRight size={16} />
            </button>

            {/* 페이지 정보 */}
            <span style={{ marginLeft: '12px', fontSize: '13px', color: '#6b7280' }}>
              {totalRegularCount}건 중 {(currentPage - 1) * 10 + 1}-{Math.min(currentPage * 10, totalRegularCount)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default AnnouncementsPage;
